import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;

function getCurrentQuarter(month) {
  if (month <= 3) return 1;
  if (month <= 6) return 2;
  if (month <= 9) return 3;
  return 4;
}

const CURRENT_QUARTER = getCurrentQuarter(CURRENT_MONTH);
const YEARS = [2024, 2025, 2026, 2027];
const QUARTERS = ["t1", "t2", "t3", "t4"];

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function quarterLabel(q) {
  return q.toUpperCase();
}

function resolverDependenciaParaApi(user) {
  const raw = user?.dependencia ?? user?.dependencia_nombre ?? user?.secretaria ?? "";
  if (raw === 1 || raw === "1") return "PLANEACION";
  if (raw === 2 || raw === "2") return "INFRAESTRUCTURA";
  const texto = String(raw).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
  if (texto.includes("PLANEACION")) return "PLANEACION";
  if (texto.includes("INFRAESTRUCTURA")) return "INFRAESTRUCTURA";
  if (texto.includes("SALUD")) return "SALUD";
  if (texto.includes("HACIENDA")) return "HACIENDA";
  if (texto.includes("COMUNICACION")) return "COMUNICACIONES";
  if (texto.includes("DESARROLLO") && texto.includes("SOCIAL")) return "DESARROLLO Y BIENESTAR SOCIAL";
  if (texto.includes("EDUCACION")) return "EDUCACION Y CULTURA";
  if (texto.includes("GOBIERNO")) return "GOBIERNO";
  if (texto.includes("MOVILIDAD")) return "MOVILIDAD";
  if (texto.includes("SERVICIO")) return "SERVICIOS ADMINISTRATIVOS";
  if (texto.includes("AGRICULTURA") || texto.includes("MEDIO AMBIENTE")) return "AGRICULTURA Y MEDIO AMBIENTE";
  if (texto.includes("INDER")) return "INDER";
  if (texto.includes("INVICOP")) return "INVICOP";
  return texto;
}

async function handleLogout() {
  try {
    await fetch("https://desarrollandoterritorios.com/api/auth/logout/", {
      method: "POST",
      credentials: "include",
    });
  } catch (e) {
    console.error(e);
  } finally {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    sessionStorage.clear();
    window.location.href = "/login";
  }
}

export default function SeguimientoPDD() {
  const navigate = useNavigate();
  const [indicadores, setIndicadores] = useState([]);
  const [expandedYearByRow, setExpandedYearByRow] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({});
  const [evidencia, setEvidencia] = useState({});

  useEffect(() => {
    const cargarIndicadores = async () => {
      setLoading(true);
      setError("");
      try {
        const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
        const dependencia = resolverDependenciaParaApi(user);
        if (!dependencia) throw new Error("No se encontró la dependencia del usuario en sesión");

        const resp = await fetch(
          `https://desarrollandoterritorios.com/api/pdd_indicadores.php?dependencia=${encodeURIComponent(dependencia)}`,
          { credentials: "include" }
        );
        if (!resp.ok) throw new Error(`Error HTTP ${resp.status}`);
        const json = await resp.json();
        if (!json.ok) throw new Error(json.message || "La API devolvió un error");

        const dataTransformada = (json.data || []).map((item) => ({
          id: Number(item.id),
          numero: item.numero_indicador,
          nombre: item.nombre_resumido || item.nombre_completo,
          nombre_completo: item.nombre_completo,
          unidad: item.unidad_medida,
          meta_cuatrenio: Number(item.meta_cuatrenio) || 0,
          years: {
            2024: {
              meta_anual: Number(item.meta_2024) || 0,
              t1: { value: item["2024_t1"] ?? "", locked: Boolean(item["2024_t1_locked"]) },
              t2: { value: item["2024_t2"] ?? "", locked: Boolean(item["2024_t2_locked"]) },
              t3: { value: item["2024_t3"] ?? "", locked: Boolean(item["2024_t3_locked"]) },
              t4: { value: item["2024_t4"] ?? "", locked: Boolean(item["2024_t4_locked"]) },
            },
            2025: {
              meta_anual: Number(item.meta_2025) || 0,
              t1: { value: item["2025_t1"] ?? "", locked: Boolean(item["2025_t1_locked"]) },
              t2: { value: item["2025_t2"] ?? "", locked: Boolean(item["2025_t2_locked"]) },
              t3: { value: item["2025_t3"] ?? "", locked: Boolean(item["2025_t3_locked"]) },
              t4: { value: item["2025_t4"] ?? "", locked: Boolean(item["2025_t4_locked"]) },
            },
            2026: {
              meta_anual: Number(item.meta_2026) || 0,
              t1: { value: item["2026_t1"] ?? "", locked: Boolean(item["2026_t1_locked"]) },
              t2: { value: item["2026_t2"] ?? "", locked: Boolean(item["2026_t2_locked"]) },
              t3: { value: item["2026_t3"] ?? "", locked: Boolean(item["2026_t3_locked"]) },
              t4: { value: item["2026_t4"] ?? "", locked: Boolean(item["2026_t4_locked"]) },
            },
            2027: {
              meta_anual: Number(item.meta_2027) || 0,
              t1: { value: item["2027_t1"] ?? "", locked: Boolean(item["2027_t1_locked"]) },
              t2: { value: item["2027_t2"] ?? "", locked: Boolean(item["2027_t2_locked"]) },
              t3: { value: item["2027_t3"] ?? "", locked: Boolean(item["2027_t3_locked"]) },
              t4: { value: item["2027_t4"] ?? "", locked: Boolean(item["2027_t4_locked"]) },
            },
          },
        }));

        setIndicadores(dataTransformada);
      } catch (err) {
        setError(err.message || "Error cargando indicadores");
        setIndicadores([]);
      } finally {
        setLoading(false);
      }
    };
    cargarIndicadores();
  }, []);

  const toggleYear = (indicadorId, year) => {
    setExpandedYearByRow((prev) => ({
      ...prev,
      [indicadorId]: prev[indicadorId] === year ? null : year,
    }));
  };

  const isYearPast = (year) => year < CURRENT_YEAR;
  const isYearFuture = (year) => year > CURRENT_YEAR;

  const isQuarterEnabled = (year, quarterKey, quarterData) => {
    if (quarterData.locked) return false;
    if (isYearPast(year)) return false;
    if (isYearFuture(year)) return false;
    return Number(quarterKey.replace("t", "")) <= CURRENT_QUARTER;
  };

  const handleChange = (indicadorId, year, quarterKey, value) => {
    setIndicadores((prev) =>
      prev.map((ind) => {
        if (ind.id !== indicadorId) return ind;
        return {
          ...ind,
          years: {
            ...ind.years,
            [year]: {
              ...ind.years[year],
              [quarterKey]: { ...ind.years[year][quarterKey], value },
            },
          },
        };
      })
    );
  };

  const guardarTrimestre = async (indicadorId, year, quarterKey) => {
    const saveKey = `${indicadorId}_${year}_${quarterKey}`;
    const indicador = indicadores.find((i) => i.id === indicadorId);
    if (!indicador) return;

    const quarter = indicador.years[year][quarterKey];
    if (quarter.value === "" || quarter.value === null) return;

    setSaving((prev) => ({ ...prev, [saveKey]: true }));

    try {
      const trimestre = Number(quarterKey.replace("t", ""));
      const valor = Number(quarter.value);
      if (!Number.isFinite(valor)) throw new Error("El valor ingresado no es válido");

      const ev = evidencia[saveKey] || {};

      const resp = await fetch(
        "https://desarrollandoterritorios.com/api/pdd_guardar_seguimiento.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            pdd_id: indicadorId,
            anio: year,
            trimestre,
            valor,
            ubicacion_evidencia: ev.ubicacion ?? "",
            tipo_evidencia: ev.tipo ?? "",
          }),
        }
      );

      const text = await resp.text();
      let json = null;
      try { json = JSON.parse(text); }
      catch { throw new Error(`El endpoint no devolvió JSON. HTTP ${resp.status}`); }
      if (!resp.ok || !json.ok) throw new Error(json?.message || `Error HTTP ${resp.status}`);

      setIndicadores((prev) =>
        prev.map((item) => {
          if (item.id !== indicadorId) return item;
          return {
            ...item,
            years: {
              ...item.years,
              [year]: {
                ...item.years[year],
                [quarterKey]: { ...item.years[year][quarterKey], locked: true },
              },
            },
          };
        })
      );
    } catch (error) {
      alert(error.message || "Ocurrió un error al guardar");
    } finally {
      setSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const calcularTotalAnual = (indicador, year) => {
    const yearData = indicador.years[year];
    return QUARTERS.reduce((acc, q) => {
      const qd = yearData[q];
      return acc + (qd?.locked ? toNum(qd.value) : 0);
    }, 0);
  };

  const calcularTotalIndicador = (indicador) => {
    return YEARS.reduce((accYear, year) => {
      return accYear + calcularTotalAnual(indicador, year);
    }, 0);
  };

  const calcularAvanceTotal = (indicador) => {
    const total = calcularTotalIndicador(indicador);
    if (!toNum(indicador.meta_cuatrenio)) return 0;
    return ((total / toNum(indicador.meta_cuatrenio)) * 100).toFixed(0);
  };

  const dependenciaActual = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
      return user?.dependencia_nombre ?? user?.secretaria_nombre ?? user?.dependencia ?? user?.secretaria ?? "Sin dependencia";
    } catch { return "Sin dependencia"; }
  }, []);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.wrapper}>
          <div style={styles.card}>
            <div style={styles.emptyCell}>Cargando seguimiento del plan de desarrollo...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.wrapper}>

        <div style={styles.header}>
          <div style={styles.headerTop}>
            <button type="button" onClick={handleLogout} style={styles.logoutButton}>
              Cerrar sesión
            </button>
          </div>
          <div>
            <h1 style={styles.title}>Seguimiento Plan de Desarrollo</h1>
            <p style={styles.subtitle}>Dependencia: {dependenciaActual}</p>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.theadRow}>
                  <th style={styles.th}>Indicador</th>
                  <th style={styles.th}>Meta Cuatrienio</th>
                  {YEARS.map((year) => <th key={year} style={styles.th}>{year}</th>)}
                  <th style={styles.th}>Progreso Total</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr><td colSpan={7} style={styles.emptyCell}>Error: {error}</td></tr>
                ) : indicadores.length === 0 ? (
                  <tr><td colSpan={7} style={styles.emptyCell}>No hay indicadores para esta dependencia.</td></tr>
                ) : (
                  indicadores.map((indicador) => {
                    const expandedYear = expandedYearByRow[indicador.id];
                    return (
                      <React.Fragment key={indicador.id}>
                        <tr style={styles.mainRow}>
                          <td style={styles.tdIndicator}>
                            <div
                              title={indicador.nombre_completo || indicador.nombre}
                              onClick={() => navigate(`/seguimiento-pdd/indicador/${indicador.id}`)}
                              style={{ cursor: "pointer" }}
                            >
                              <strong>{indicador.numero}</strong>
                              <div style={styles.indicadorNombre}>{indicador.nombre}</div>
                            </div>
                          </td>
                          <td style={styles.tdCentered}>{indicador.meta_cuatrenio}</td>

                          {YEARS.map((year) => {
                            const totalAnual = calcularTotalAnual(indicador, year);
                            const metaAnual = indicador.years[year]?.meta_anual ?? 0;
                            return (
                              <td key={year} style={styles.tdCentered}>
                                <button
                                  type="button"
                                  onClick={() => toggleYear(indicador.id, year)}
                                  style={{
                                    ...styles.yearButton,
                                    ...(expandedYear === year ? styles.yearButtonActive : {}),
                                  }}
                                >
                                  <div style={{ fontWeight: 700 }}>{year}</div>
                                  <div style={{ fontSize: 12, marginTop: 4 }}>Meta: {metaAnual}</div>
                                  <div style={{ fontSize: 12 }}>Ejecutado: {totalAnual}</div>
                                </button>
                              </td>
                            );
                          })}

                          <td style={styles.tdProgress}>
                            <div style={styles.progressText} title="Cupo registrado vs meta cuatrienio">
                              {calcularAvanceTotal(indicador)}%
                              <div style={{ fontSize: "10px", color: "#6b7280", fontWeight: 400, marginTop: "2px" }}>
                                cupo/meta
                              </div>
                            </div>
                            <div style={styles.progressBarBg}>
                              <div style={{
                                ...styles.progressBarFill,
                                width: `${Math.min(Number(calcularAvanceTotal(indicador)), 100)}%`,
                              }} />
                            </div>
                          </td>
                        </tr>

                        {expandedYear && (
                          <tr>
                            <td colSpan={7} style={styles.expandCell}>
                              <div style={styles.expandBox}>
                                <div style={styles.expandTitle}>
                                  Año {expandedYear} — Cupo trimestral (meta por trimestre)
                                </div>
                                <div style={styles.quartersGrid}>
                                  {QUARTERS.map((quarterKey) => {
                                    const quarterData = indicador.years[expandedYear][quarterKey];
                                    const enabled = isQuarterEnabled(expandedYear, quarterKey, quarterData);
                                    const saveKey = `${indicador.id}_${expandedYear}_${quarterKey}`;
                                    const isSaving = Boolean(saving[saveKey]);
                                    const ev = evidencia[saveKey] || {};

                                    return (
                                      <div key={quarterKey} style={styles.quarterCard}>
                                        <div style={styles.quarterTitle}>
                                          {quarterLabel(quarterKey)} — Cupo trimestral
                                        </div>

                                        <input
                                          type="number"
                                          value={quarterData.value}
                                          disabled={!enabled || isSaving}
                                          onChange={(e) => handleChange(indicador.id, expandedYear, quarterKey, e.target.value)}
                                          style={{ ...styles.input, ...(!enabled || isSaving ? styles.inputDisabled : {}) }}
                                          placeholder="Ej: 5 (cupo de actividades)"
                                        />

                                        <div style={styles.statusRow}>
                                          {quarterData.locked ? (
                                            <span style={styles.lockedBadge}>Guardado</span>
                                          ) : isSaving ? (
                                            <span style={styles.pendingBadge}>Guardando...</span>
                                          ) : enabled ? (
                                            <span style={styles.openBadge}>Editable</span>
                                          ) : isYearPast(expandedYear) ? (
                                            <span style={styles.lockedBadge}>Año cerrado</span>
                                          ) : isYearFuture(expandedYear) ? (
                                            <span style={styles.futureBadge}>Año futuro</span>
                                          ) : (
                                            <span style={styles.pendingBadge}>Pendiente</span>
                                          )}
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() => guardarTrimestre(indicador.id, expandedYear, quarterKey)}
                                          disabled={quarterData.locked || !enabled || quarterData.value === "" || isSaving}
                                          style={{
                                            ...styles.saveButton,
                                            ...(quarterData.locked || !enabled || quarterData.value === "" || isSaving
                                              ? styles.saveButtonDisabled : {}),
                                          }}
                                        >
                                          {isSaving ? "Guardando..." : "Guardar"}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page:             { minHeight: "100vh", background: "#f5f7fb", padding: "24px" },
  wrapper:          { maxWidth: "1500px", margin: "0 auto" },
  header:           { marginBottom: "18px" },
  headerTop:        { marginBottom: "14px", display: "flex", justifyContent: "flex-end" },
  logoutButton:     { border: "none", borderRadius: "10px", background: "#ef4444", color: "#fff", padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: "14px" },
  title:            { margin: 0, fontSize: "32px", fontWeight: 800, color: "#1f3b64" },
  subtitle:         { marginTop: "8px", color: "#5b6472", fontSize: "15px" },
  card:             { background: "#ffffff", borderRadius: "16px", boxShadow: "0 4px 18px rgba(0,0,0,0.06)", padding: "16px" },
  table:            { width: "100%", borderCollapse: "collapse", minWidth: "1200px" },
  theadRow:         { background: "#1677c8" },
  th:               { color: "#fff", padding: "16px 12px", textAlign: "center", fontWeight: 700, fontSize: "15px", border: "1px solid #d9e2f1" },
  tdIndicator:      { border: "1px solid #d9e2f1", padding: "14px 12px", verticalAlign: "top", minWidth: "300px" },
  indicadorNombre:  { marginTop: "6px", color: "#2e3848", lineHeight: 1.4 },
  tdCentered:       { border: "1px solid #d9e2f1", padding: "10px", textAlign: "center", verticalAlign: "middle" },
  tdProgress:       { border: "1px solid #d9e2f1", padding: "10px 12px", minWidth: "160px" },
  progressText:     { fontWeight: 700, marginBottom: "6px", textAlign: "center" },
  progressBarBg:    { height: "8px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden" },
  progressBarFill:  { height: "8px", background: "#16a34a", borderRadius: "999px" },
  mainRow:          { background: "#fff" },
  yearButton:       { border: "1px solid #d1d5db", borderRadius: "10px", background: "#f8fafc", padding: "10px 12px", width: "100%", cursor: "pointer", color: "#1f2937" },
  yearButtonActive: { background: "#e0f2fe", border: "1px solid #38bdf8" },
  expandCell:       { padding: "0", border: "1px solid #d9e2f1", background: "#f8fbff" },
  expandBox:        { padding: "16px" },
  expandTitle:      { fontWeight: 700, color: "#1f3b64", marginBottom: "14px" },
  quartersGrid:     { display: "grid", gridTemplateColumns: "repeat(4, minmax(180px, 1fr))", gap: "12px" },
  quarterCard:      { border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", padding: "14px" },
  quarterTitle:     { fontWeight: 700, marginBottom: "10px", color: "#1f2937" },
  input:            { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box" },
  inputDisabled:    { background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" },
  statusRow:        { minHeight: "24px", marginBottom: "10px" },
  lockedBadge:      { fontSize: "12px", color: "#991b1b", background: "#fee2e2", padding: "4px 8px", borderRadius: "999px" },
  openBadge:        { fontSize: "12px", color: "#166534", background: "#dcfce7", padding: "4px 8px", borderRadius: "999px" },
  pendingBadge:     { fontSize: "12px", color: "#92400e", background: "#fef3c7", padding: "4px 8px", borderRadius: "999px" },
  futureBadge:      { fontSize: "12px", color: "#1d4ed8", background: "#dbeafe", padding: "4px 8px", borderRadius: "999px" },
  saveButton:       { width: "100%", border: "none", borderRadius: "8px", background: "#1677c8", color: "#fff", padding: "10px 12px", cursor: "pointer", fontWeight: 700 },
  saveButtonDisabled: { background: "#94a3b8", cursor: "not-allowed" },
  emptyCell:        { textAlign: "center", padding: "24px", color: "#64748b", border: "1px solid #d9e2f1" },
};